/**
 * Jobs SEO Page
 * 
 * Dynamic SEO landing pages for jobs by country and category.
 * URLs: /jobs/{country}, /jobs/{country}/{category}
 * 
 * Includes Schema.org structured data for rich snippets.
 */

import React, { useState, useEffect } from 'react';
import { useParams, Link, useNavigate, useLocation } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { useLanguage, useAuth } from '@/lib/context';
import { LanguageSwitcher } from '@/components/LanguageSwitcher';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Briefcase, MapPin, Star, Clock, ArrowRight, Search, 
  ChevronRight, Shield, CheckCircle2, Building2, Users, Globe
} from 'lucide-react';
import { BreadcrumbSchema, ItemListSchema, WebPageSchema } from '@/components/StructuredData';
import { TrustScoreBadge } from '@/components/TrustScoreBadge';

const API = process.env.REACT_APP_BACKEND_URL;

// Job categories with i18n keys
const JOB_CATEGORIES = [
  { value: 'construction', labelKey: 'jobs.categories.construction', icon: '🏗️' },
  { value: 'manufacturing', labelKey: 'jobs.categories.manufacturing', icon: '🏭' },
  { value: 'logistics', labelKey: 'jobs.categories.logistics', icon: '📦' },
  { value: 'healthcare', labelKey: 'jobs.categories.healthcare', icon: '🏥' },
  { value: 'hospitality', labelKey: 'jobs.categories.hospitality', icon: '🏨' },
  { value: 'retail', labelKey: 'jobs.categories.retail', icon: '🛒' },
  { value: 'technology', labelKey: 'jobs.categories.technology', icon: '💻' },
  { value: 'agriculture', labelKey: 'jobs.categories.agriculture', icon: '🌾' },
  { value: 'cleaning', labelKey: 'jobs.categories.cleaning', icon: '🧹' },
  { value: 'transportation', labelKey: 'jobs.categories.transportation', icon: '🚛' }
];

// Countries with i18n keys and slugs
const COUNTRIES = [
  { slug: 'finland', code: 'FI', labelKey: 'countries.FI', flag: '🇫🇮' },
  { slug: 'estonia', code: 'EE', labelKey: 'countries.EE', flag: '🇪🇪' },
  { slug: 'sweden', code: 'SE', labelKey: 'countries.SE', flag: '🇸🇪' },
  { slug: 'norway', code: 'NO', labelKey: 'countries.NO', flag: '🇳🇴' },
  { slug: 'germany', code: 'DE', labelKey: 'countries.DE', flag: '🇩🇪' },
  { slug: 'denmark', code: 'DK', labelKey: 'countries.DK', flag: '🇩🇰' },
  { slug: 'netherlands', code: 'NL', labelKey: 'countries.NL', flag: '🇳🇱' },
  { slug: 'poland', code: 'PL', labelKey: 'countries.PL', flag: '🇵🇱' }
];

export default function JobsSeoPage() {
  const params = useParams();
  const location = useLocation();
  const { t, currentLanguage } = useLanguage();
  const navigate = useNavigate();
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');

  // Extract country and category from URL path
  // Supports: /jobs/finland, /jobs/finland/construction, /tood/estonia, etc.
  const pathParts = location.pathname.split('/').filter(Boolean);
  const country = params.country || (pathParts[0] === 'jobs' ? pathParts[1] : pathParts[1]) || '';
  const category = params.category || pathParts[2] || '';

  const normalizedCountry = country?.toLowerCase();
  const normalizedCategory = category?.toLowerCase();
  
  const countryData = COUNTRIES.find(c => c.slug === normalizedCountry);
  const categoryData = JOB_CATEGORIES.find(c => c.value === normalizedCategory);

  // Fetch jobs for this country/category
  useEffect(() => {
    const fetchJobs = async () => {
      setLoading(true);
      try {
        let url = `${API}/api/jobs?country=${countryData?.code || normalizedCountry}`;
        if (normalizedCategory) {
          url += `&category=${normalizedCategory}`;
        }
        url += '&limit=20';
        
        const res = await fetch(url);
        if (res.ok) {
          const data = await res.json();
          setJobs(data.jobs || data || []);
        }
      } catch (error) {
        console.error('Failed to fetch jobs:', error);
      } finally {
        setLoading(false);
      }
    };

    if (normalizedCountry) {
      fetchJobs();
    }
  }, [normalizedCountry, normalizedCategory, countryData]);

  // Get SEO data based on language
  const getSeoData = () => {
    const countryName = countryData ? t(countryData.labelKey) : normalizedCountry;
    const categoryName = categoryData ? t(categoryData.labelKey) : null;

    if (categoryName) {
      return {
        title: t('seo.jobs.titleWithCategory', { country: countryName, category: categoryName }),
        description: t('seo.jobs.descWithCategory', { country: countryName, category: categoryName }),
        h1: t('seo.jobs.h1WithCategory', { country: countryName, category: categoryName })
      };
    }
    
    return {
      title: t('seo.jobs.title', { country: countryName }),
      description: t('seo.jobs.desc', { country: countryName }),
      h1: t('seo.jobs.h1', { country: countryName })
    };
  };

  const seo = getSeoData();
  const countryName = countryData ? t(countryData.labelKey) : normalizedCountry;

  // Build canonical URL
  const canonicalUrl = normalizedCategory 
    ? `https://keikka.com/jobs/${normalizedCountry}/${normalizedCategory}`
    : `https://keikka.com/jobs/${normalizedCountry}`;

  return (
    <div className="min-h-screen bg-background">
      {/* SEO Meta Tags */}
      <Helmet>
        <title>{seo.title} | Keikka</title>
        <meta name="description" content={seo.description} />
        <link rel="canonical" href={canonicalUrl} />
        <meta property="og:title" content={seo.title} />
        <meta property="og:description" content={seo.description} />
        <meta property="og:url" content={canonicalUrl} />
        <meta property="og:type" content="website" />
        <meta name="robots" content="index, follow" />
        {/* Hreflang tags */}
        <link rel="alternate" hreflang="en" href={`https://keikka.com/en/jobs/${normalizedCountry}${normalizedCategory ? `/${normalizedCategory}` : ''}`} />
        <link rel="alternate" hreflang="et" href={`https://keikka.com/et/tood/${normalizedCountry}${normalizedCategory ? `/${normalizedCategory}` : ''}`} />
        <link rel="alternate" hreflang="fi" href={`https://keikka.com/fi/tyot/${normalizedCountry}${normalizedCategory ? `/${normalizedCategory}` : ''}`} />
        <link rel="alternate" hreflang="x-default" href={canonicalUrl} />
      </Helmet>

      {/* Schema.org Structured Data for Rich Snippets */}
      <BreadcrumbSchema 
        items={[
          { name: t('common.home'), url: 'https://keikka.com' },
          { name: t('nav.jobs'), url: 'https://keikka.com/jobs' },
          { name: countryName, url: `https://keikka.com/jobs/${normalizedCountry}` },
          ...(normalizedCategory && categoryData ? [{ name: t(categoryData.labelKey), url: canonicalUrl }] : [])
        ]} 
      />
      <WebPageSchema
        name={seo.title}
        description={seo.description}
        url={canonicalUrl}
        inLanguage={currentLanguage}
      />
      {!normalizedCategory && (
        <ItemListSchema
          name={`${t('nav.jobs')} - ${countryName}`}
          description={seo.description}
          url={canonicalUrl}
          items={JOB_CATEGORIES.slice(0, 8).map(cat => ({
            name: t(cat.labelKey),
            url: `https://keikka.com/jobs/${normalizedCountry}/${cat.value}`
          }))}
        />
      )}

      {/* Simple Navigation */}
      <nav className="sticky top-0 z-50 bg-background/95 backdrop-blur border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="text-2xl font-bold text-brand">KEIKKA</Link>
            <div className="hidden md:flex items-center gap-6">
              <Link to="/jobs" className="text-muted-foreground hover:text-foreground transition">
                {t('nav.jobs')}
              </Link>
              <Link to="/auto" className="text-muted-foreground hover:text-foreground transition">
                {t('nav.autoRepair')}
              </Link>
              <Link to="/rentals" className="text-muted-foreground hover:text-foreground transition">
                {t('nav.rentals')}
              </Link>
            </div>
            <div className="flex items-center gap-4">
              <LanguageSwitcher />
            </div>
          </div>
        </div>
      </nav>

      <main className="container mx-auto px-4 py-8">
        {/* Breadcrumb */}
        <nav className="flex items-center gap-2 text-sm text-muted-foreground mb-6">
          <Link to="/" className="hover:text-foreground">{t('common.home')}</Link>
          <ChevronRight className="h-4 w-4" />
          <Link to="/jobs" className="hover:text-foreground">{t('nav.jobs')}</Link>
          <ChevronRight className="h-4 w-4" />
          <span className="text-foreground">{countryData?.flag} {countryName}</span>
          {normalizedCategory && (
            <>
              <ChevronRight className="h-4 w-4" />
              <span className="text-foreground">
                {categoryData?.icon} {t(categoryData?.labelKey || normalizedCategory)}
              </span>
            </>
          )}
        </nav>

        {/* Hero Section */}
        <section className="mb-12">
          <div className="bg-gradient-to-r from-brand/10 to-green-500/10 rounded-2xl p-8 md:p-12">
            <div className="flex items-center gap-2 text-brand mb-4">
              <Globe className="h-5 w-5" />
              <span className="font-medium">{countryData?.flag} {countryName}</span>
            </div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4">
              {seo.h1}
            </h1>
            <p className="text-lg text-muted-foreground mb-6 max-w-2xl">
              {seo.description}
            </p>
            
            {/* Trust Badges */}
            <div className="flex flex-wrap gap-4 mb-8">
              <div className="flex items-center gap-2 text-sm">
                <Shield className="h-4 w-4 text-green-600" />
                <span>{t('jobs.seo.verifiedJobs')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span>{t('jobs.seo.noRegistration')}</span>
              </div>
              <div className="flex items-center gap-2 text-sm">
                <Users className="h-4 w-4 text-blue-500" />
                <span>{t('jobs.seo.directContact')}</span>
              </div>
            </div>

            {/* Quick Search */}
            <div className="flex flex-col sm:flex-row gap-3 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input
                  placeholder={t('jobs.seo.searchPlaceholder')}
                  className="pl-10"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <Button 
                size="lg" 
                onClick={() => navigate(`/jobs?country=${countryData?.code || normalizedCountry}&search=${searchQuery}`)}
                data-testid="search-jobs-btn"
              >
                {t('jobs.seo.searchJobs')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Categories Grid (if no specific category selected) */}
        {!normalizedCategory && (
          <section className="mb-12">
            <h2 className="text-2xl font-bold mb-6">{t('jobs.seo.browseByCategory')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-4">
              {JOB_CATEGORIES.map((cat) => (
                <Link
                  key={cat.value}
                  to={`/jobs/${normalizedCountry}/${cat.value}`}
                  className="flex flex-col items-center p-4 rounded-xl border hover:border-brand hover:bg-brand/5 transition-all group"
                  data-testid={`category-${cat.value}`}
                >
                  <span className="text-3xl mb-2">{cat.icon}</span>
                  <span className="text-sm font-medium text-center group-hover:text-brand">
                    {t(cat.labelKey)}
                  </span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Jobs List */}
        <section className="mb-12">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-2xl font-bold">
              {t('jobs.seo.latestJobs', { country: countryName, count: jobs.length })}
            </h2>
            <Badge variant="outline">{jobs.length} {t('jobs.seo.openPositions')}</Badge>
          </div>

          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <Card key={i} className="animate-pulse">
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-3/4 mb-4"></div>
                    <div className="h-4 bg-muted rounded w-1/2 mb-2"></div>
                    <div className="h-4 bg-muted rounded w-2/3"></div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : jobs.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center">
                <Briefcase className="h-12 w-12 mx-auto mb-4 text-muted-foreground" />
                <h3 className="text-lg font-medium mb-2">{t('jobs.seo.noJobsYet')}</h3>
                <p className="text-muted-foreground mb-4">
                  {t('jobs.seo.noJobsDesc', { country: countryName })}
                </p>
                <Button onClick={() => navigate('/jobs')}>
                  {t('jobs.seo.browseAllJobs')}
                </Button>
              </CardContent>
            </Card>
          ) : (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {jobs.map((job) => (
                <Card 
                  key={job.id || job._id} 
                  className="hover:shadow-lg transition-shadow cursor-pointer"
                  onClick={() => navigate(`/jobs/${job.id || job._id}`)}
                  data-testid={`job-${job.id || job._id}`}
                >
                  <CardHeader>
                    <div className="flex items-start justify-between">
                      <div className="flex-1 min-w-0">
                        <CardTitle className="text-lg line-clamp-2">{job.title}</CardTitle>
                        <CardDescription className="flex items-center gap-1 mt-1">
                          <Building2 className="h-3 w-3" />
                          {job.company_name || job.company?.name || t('jobs.seo.employer')}
                        </CardDescription>
                      </div>
                      {job.trust_score && (
                        <TrustScoreBadge score={job.trust_score} size="sm" t={t} />
                      )}
                    </div>
                  </CardHeader>
                  <CardContent>
                    {/* Location & Type */}
                    <div className="flex flex-wrap gap-2 mb-4">
                      <Badge variant="outline" className="text-xs">
                        <MapPin className="h-3 w-3 mr-1" />
                        {job.location || job.city || countryName}
                      </Badge>
                      {job.job_type && (
                        <Badge variant="secondary" className="text-xs">
                          {job.job_type}
                        </Badge>
                      )}
                    </div>
                    
                    {/* Quick Info */}
                    <div className="flex items-center gap-4 text-sm text-muted-foreground">
                      <span className="flex items-center gap-1">
                        <Clock className="h-3 w-3" />
                        {job.created_at ? new Date(job.created_at).toLocaleDateString() : t('jobs.seo.recent')}
                      </span>
                      {job.average_rating > 0 && (
                        <span className="flex items-center gap-1 text-yellow-600">
                          <Star className="h-3 w-3 fill-yellow-500" />
                          {job.average_rating.toFixed(1)}
                        </span>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          {/* View All Button */}
          {jobs.length > 0 && (
            <div className="text-center mt-8">
              <Button 
                variant="outline" 
                size="lg"
                onClick={() => navigate(`/jobs?country=${countryData?.code || normalizedCountry}${normalizedCategory ? `&category=${normalizedCategory}` : ''}`)}
              >
                {t('jobs.seo.viewAllJobs')}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
            </div>
          )}
        </section>

        {/* ============================================================
            HUMAN-GRADE SEO CONTENT BLOCKS
            ============================================================ */}
        
        {/* Local Context Block - Country-specific information */}
        <section className="mb-12 bg-muted/30 rounded-2xl p-6 md:p-8">
          <h2 className="text-xl font-bold mb-4">
            {t('jobs.seo.workingIn', { country: countryName })}
          </h2>
          <div className="prose prose-sm max-w-none text-muted-foreground space-y-4">
            <p>{t(`jobs.seo.contentBlocks.heroIntro.${normalizedCountry}`, { defaultValue: t('jobs.seo.contentBlocks.heroIntro.finland') })}</p>
            <p>{t(`jobs.seo.pageContent.workCulture.${normalizedCountry}`, { defaultValue: t('jobs.seo.pageContent.workCulture.finland') })}</p>
            <p>{t(`jobs.seo.pageContent.wages.${normalizedCountry}`, { defaultValue: t('jobs.seo.pageContent.wages.finland') })}</p>
          </div>
        </section>

        {/* Category Explanation Block (if category selected) */}
        {normalizedCategory && categoryData && (
          <section className="mb-12">
            <h2 className="text-xl font-bold mb-4">
              {t('jobs.seo.aboutCategory', { category: t(categoryData.labelKey) })}
            </h2>
            <div className="prose prose-sm max-w-none text-muted-foreground">
              <p>{t(`jobs.seo.contentBlocks.categoryExplanation.${normalizedCategory}`, { defaultValue: '' })}</p>
            </div>
          </section>
        )}

        {/* Internal Linking Section - Related Categories */}
        {!normalizedCategory && (
          <section className="mb-12">
            <h2 className="text-xl font-bold mb-4">{t('jobs.seo.contentBlocks.relatedCategories')}</h2>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
              {JOB_CATEGORIES.slice(0, 8).map((cat) => (
                <Link
                  key={cat.value}
                  to={`/jobs/${normalizedCountry}/${cat.value}`}
                  className="flex items-center gap-2 p-3 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all"
                  data-testid={`related-cat-${cat.value}`}
                >
                  <span>{cat.icon}</span>
                  <span className="text-sm">{t(cat.labelKey)}</span>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* Internal Linking - Related Services */}
        <section className="mb-12">
          <h2 className="text-xl font-bold mb-4">{t('jobs.seo.contentBlocks.relatedServices')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <Link
              to={`/services/${normalizedCountry}`}
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-services"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.services')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('jobs.seo.findProviders', { country: countryName })}</p>
            </Link>
            <Link
              to={`/rentals/${normalizedCountry}`}
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-rentals"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.rentals')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('jobs.seo.rentEquipment', { country: countryName })}</p>
            </Link>
            <Link
              to="/subcontracting"
              className="p-4 rounded-lg border hover:border-brand hover:bg-brand/5 transition-all group"
              data-testid="link-subcontracting"
            >
              <h3 className="font-medium group-hover:text-brand">{t('nav.subcontracting')}</h3>
              <p className="text-sm text-muted-foreground mt-1">{t('jobs.seo.findContracts')}</p>
            </Link>
          </div>
        </section>

        {/* Other Countries */}
        <section className="mb-12">
          <h2 className="text-xl font-bold mb-4">{t('jobs.seo.otherCountries')}</h2>
          <div className="flex flex-wrap gap-2">
            {COUNTRIES
              .filter(c => c.slug !== normalizedCountry)
              .map((otherCountry) => (
                <Link
                  key={otherCountry.slug}
                  to={`/jobs/${otherCountry.slug}${normalizedCategory ? `/${normalizedCategory}` : ''}`}
                  className="px-4 py-2 rounded-full border hover:border-brand hover:bg-brand/5 transition-all text-sm"
                >
                  {otherCountry.flag} {t(otherCountry.labelKey)}
                </Link>
              ))}
          </div>
        </section>

        {/* CTA Section */}
        <section className="bg-brand/5 rounded-2xl p-8 text-center">
          <h2 className="text-2xl font-bold mb-2">{t('jobs.seo.ctaTitle')}</h2>
          <p className="text-muted-foreground mb-6 max-w-xl mx-auto">
            {t('jobs.seo.ctaDesc')}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button 
              size="lg" 
              onClick={() => navigate('/register')}
            >
              {t('jobs.seo.createProfile')}
            </Button>
            <Button 
              variant="outline" 
              size="lg"
              onClick={() => navigate('/jobs')}
            >
              {t('jobs.seo.browseJobs')}
            </Button>
          </div>
        </section>
      </main>

      {/* Simple Footer */}
      <footer className="border-t mt-12 py-8">
        <div className="container mx-auto px-4 text-center text-muted-foreground">
          <p>© {new Date().getFullYear()} Keikka. {t('footer.allRightsReserved')}</p>
        </div>
      </footer>
    </div>
  );
}
